/* TagsHub — `/tags`: every public recipe tag A–Z with counts, a "most popular" strip, and a filter box.
 * Plain <a> links to /recipes/tag/:slug so crawlers can follow every tag page. */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";

function useTagsSchema(tags) {
  useEffect(() => {
    if (!tags.length) return undefined;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "rf-tags-hub-jsonld";
    const origin = window.location.origin;
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "All recipe tags",
      url: `${origin}/tags`,
      description: `Browse ${tags.length} recipe tags on Recipe Filer.`,
      isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: tags.length,
        itemListElement: tags.slice(0, 500).map((t, i) => ({
          "@type": "ListItem", position: i + 1, name: `${t.tag} recipes`, url: `${origin}/recipes/tag/${encodeURIComponent(t.slug)}`,
        })),
      },
    });
    document.head.appendChild(s);
    return () => s.remove();
  }, [tags]);
}

const TagLink = ({ t, big = false }) => (
  <Link to={`/recipes/tag/${encodeURIComponent(t.slug)}`} data-testid={`tags-hub-link-${t.slug}`}
    className={`inline-flex items-baseline gap-1.5 rounded-full border border-rf-ink/15 bg-white hover:border-rf-orange hover:text-rf-orange transition-colors ${big ? "px-4 py-2 text-base font-bold" : "px-3 py-1.5 text-sm font-semibold"} text-rf-ink`}>
    {t.tag} <span className="text-xs text-rf-ink-soft font-semibold">{t.count}</span>
  </Link>
);

export default function TagsHub() {
  const [tags, setTags] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/directory/tags-hub").then(({ data }) => setTags(data.tags || [])).catch(() => setTags([]));
  }, []);
  useTagsSchema(tags || []);

  const popular = useMemo(() => [...(tags || [])].sort((a, b) => b.count - a.count).slice(0, 16), [tags]);
  const letters = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const map = {};
    (tags || []).filter((t) => !needle || t.tag.toLowerCase().includes(needle)).forEach((t) => {
      const L = /[a-z]/i.test(t.tag[0]) ? t.tag[0].toUpperCase() : "#";
      (map[L] = map[L] || []).push(t);
    });
    return Object.entries(map).sort(([a], [b]) => (a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b)));
  }, [tags, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="tags-hub">
      <PageMeta
        title={tags ? `All recipe tags — browse ${tags.length} topics` : "All recipe tags"}
        description="Browse every recipe tag on Recipe Filer — from baking to Sunday roasts — with the number of family recipes in each."
      />
      <Link to="/directory" className="text-sm font-bold text-rf-orange hover:underline mb-4 inline-block">← All recipes</Link>
      <p className="overline mb-3">Browse</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]" data-testid="tags-hub-heading">All recipe tags</h1>
      <p className="text-rf-ink-soft text-lg mb-10 max-w-2xl" data-testid="tags-hub-count">
        {tags ? `${tags.length} tags across the public family recipes on Recipe Filer. Tap any tag to see its recipes.` : "Loading tags…"}
      </p>

      {tags && tags.length > 0 && (
        <>
          <section aria-labelledby="popular-tags" className="mb-12">
            <h2 id="popular-tags" className="font-display text-lg font-black text-rf-ink mb-3">Most popular</h2>
            <div className="flex flex-wrap gap-2" data-testid="tags-hub-popular">{popular.map((t) => <TagLink key={t.slug} t={t} big />)}</div>
          </section>

          <div className="flex flex-wrap items-center gap-4 mb-8">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" aria-hidden="true" />
              <label htmlFor="tags-hub-filter" className="sr-only">Filter tags</label>
              <input id="tags-hub-filter" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter tags…" data-testid="tags-hub-filter"
                className="rounded-full border border-rf-ink/20 pl-9 pr-4 py-2 text-sm w-full sm:w-72 bg-white" />
            </div>
            <nav aria-label="Jump to letter" className="flex flex-wrap gap-1 mobile-scroll-row" data-testid="tags-hub-letters">
              {letters.map(([L]) => (
                <a key={L} href={`#tags-${L}`} className="w-10 h-10 sm:w-7 sm:h-7 inline-flex items-center justify-center rounded-md text-xs font-black text-rf-ink-soft hover:bg-rf-cream-alt hover:text-rf-ink">{L}</a>
              ))}
            </nav>
          </div>

          {letters.length ? letters.map(([L, list]) => (
            <section key={L} id={`tags-${L}`} aria-labelledby={`tags-h-${L}`} className="mb-8 scroll-mt-24" data-testid={`tags-hub-letter-${L}`}>
              <h2 id={`tags-h-${L}`} className="font-display text-2xl font-black text-rf-ink mb-3 border-b border-rf-ink/10 pb-1">{L}</h2>
              <div className="flex flex-wrap gap-2">{list.map((t) => <TagLink key={t.slug} t={t} />)}</div>
            </section>
          )) : <p className="text-rf-ink-soft" data-testid="tags-hub-no-match">No tags match &ldquo;{q}&rdquo;.</p>}
        </>
      )}
      {tags && tags.length === 0 && <p className="text-rf-ink-soft" data-testid="tags-hub-empty">No public tags yet.</p>}
    </div>
  );
}
